/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, LinearProgress } from '@mui/material';
import React from 'react';
import type { Position, HeaderSize } from '@opengeoweb/shared';
import {
  ToolContainerDraggable,
  renderCounter,
  useWheelStopPropagation,
  AlertBanner,
} from '@opengeoweb/shared';
import type { uiTypes } from '@opengeoweb/store';
import { AreaFilterConnect } from './AreaFilter/AreaFilterConnect';
import { ConceptListConnect } from './ConceptList/ConceptListConnect';
import { DataExplorerFiltersConnect } from './DataExplorerFilters/DataExplorerFiltersConnect';
import { DataExplorerSearchConnect } from './DataExplorerFilters/DataExplorerSearchConnect';
import { DatasetListConnect } from './DatasetList/DatasetListConnect';
import { useDataExplorerTranslation } from '../utils/i18n';

interface DataExplorerProps {
  bounds?: string;
  onClose: () => void;
  onMouseDown?: () => void;
  isOpen: boolean;
  order?: number;
  source?: uiTypes.Source;
  startPosition?: Position;
  headerSize?: HeaderSize;
  loading?: boolean;
  error?: boolean;
  compact?: boolean;
}

const DataExplorer: React.FC<DataExplorerProps> = ({
  bounds,
  onClose,
  isOpen,
  onMouseDown,
  order,
  source = 'app',
  startPosition = { top: 85, left: 50 },
  headerSize = 'medium',
  loading = false,
  error = false,
  compact = false,
}: DataExplorerProps) => {
  const { t } = useDataExplorerTranslation();
  const minSize = { width: 340, height: 300 };
  renderCounter.count(DataExplorer.name);

  const title = 'MeteoGate Data Explorer';

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  return (
    <ToolContainerDraggable
      bounds={bounds}
      className="dataExplorer"
      data-testid="dataExplorerWindow"
      headerSize={headerSize}
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={
        compact ? { width: 340, height: 700 } : { width: 900, height: 520 }
      }
      title={title}
    >
      <Box
        onKeyDown={(event): void => {
          event.stopPropagation();
        }}
        ref={stopWheelEventPropagationRef}
        sx={{ overflow: 'hidden', height: '100%' }}
      >
        {!!loading && (
          <LinearProgress
            color="secondary"
            sx={{ width: '100%', position: 'absolute', top: 0, left: 0 }}
          />
        )}
        {!!error && (
          <AlertBanner shouldClose title={t('dataexplorer-error-title')} />
        )}
        <Grid container sx={{ overflow: 'hidden', height: '100%' }}>
          <Grid size={12}>
            <DataExplorerSearchConnect />
          </Grid>
          <Grid
            size={3}
            style={{
              overflowY: 'auto',
              height: 'calc(100% - 70px)',
              paddingLeft: '12px',
              minWidth: '170px',
            }}
          >
            <DataExplorerFiltersConnect />

            <AreaFilterConnect />
          </Grid>
          <Grid
            container
            size="grow"
            sx={{
              flexDirection: 'column',
              overflow: 'hidden',
              height: 'calc(100% - 70px)',
            }}
          >
            <Grid
              size={12}
              sx={{
                paddingLeft: '6px',
                marginTop: '4px',
                marginBottom: '4px',
                flex: '0 0 auto',
              }}
            >
              <ConceptListConnect />
            </Grid>
            <Grid
              size={12}
              sx={{
                overflowY: 'hidden',
                flex: '1 1 0%',
                minHeight: 0,
              }}
            >
              <DatasetListConnect />
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </ToolContainerDraggable>
  );
};

export default DataExplorer;
